import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import WorkerProfile from './WorkerProfile';
import FeatureBox from './FeatureBox';
import { RainbowButton } from '../ui/rainbow-button';
import { motion, AnimatePresence } from 'framer-motion';
import WorkerVideo from './WorkerVideo';
import ImmersiveWorkerView from './ImmersiveWorkerView';
import { useViewpoint } from '../../contexts/ViewpointContext';
import { useTheme } from '../../contexts/ThemeContext';

// Theme variables for spacing control
const theme = {
  // Space between sections
  spacing: {
    // Space between title and subtitle
    titleToSubtitle: '32px',
    // Space between subtitle and department selector
    subtitleToDepartments: '64px',
    // Space between department selector and worker cards
    departmentToWorkers: '48px',
    // Space after the worker section (before the next section/separator)
    sectionBottom: '0px',
  },
  // Space between left and right sections
  sectionGap: '32px',
  // Top margin of the right section (can be negative)
  rightSectionTopMargin: '4px',
  // Gap between feature boxes horizontally
  featureBoxHorizontalGap: '24px',
  // Gap between feature boxes vertically
  featureBoxVerticalGap: '40px',
  // Overall content top margin
  contentTopMargin: '48px',
  workerCard: {
    // Base styles
    background: {
      active: 'rgba(0, 0, 0, 0.5)',      // More transparent to show glass effect
      inactive: 'rgba(255, 255, 255, 0.3)', // More transparent to show glass effect
      activeHover: 'rgba(0, 0, 0, 0.2)',
      inactiveHover: 'rgba(255, 255, 255, 0.1)',
    },
    text: {
      active: '#ffffff',
      inactive: '#000000',
      activeOpacity: 1,
      inactiveOpacity: 0.9,
    },
    border: {
      active: 'rgba(255, 255, 255, 0.2)',
      inactive: 'rgba(0, 0, 0, 0.1)',
    },
    glass: {
      blur: '12px',
      opacity: 0.6,
    },
    hover: {
      transform: 'scale(1.02) translateY(-4px)',
      transition: 'all 0.3s cubic-bezier(0.3, 0, 0.2, 1)',
      shadow: '0 20px 40px rgba(0, 0, 0, 0.2), 0 15px 20px rgba(0, 0, 0, 0.1)',
    },
    gradient: {
      active: 'rgba(0, 0, 0, 0.8)',
      inactive: 'rgb(255, 255, 255)',
      opacity: {
        active: 0.8,
        inactive: 0.5,
      }
    },
    image: {
      opacity: {
        active: 0.7,        // More visible when active
        inactive: 0.3,      // More visible when inactive
        activeHover: 0.6,   // Increases on hover when active
        inactiveHover: 0.5, // Increases on hover when inactive
      }
    }
  },
};

const Section = styled.section`
  width: 100%;
  padding-top: 90px;
  padding-bottom: ${theme.spacing.sectionBottom};
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  width: 100%;
`;

const Title = styled.h2`
  font-size: 72px;
  font-weight: 600;
  text-align: center;
  margin-bottom: ${theme.spacing.titleToSubtitle};
  letter-spacing: -0.02em;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 48px;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: 20px;
  color: #666;
  margin-bottom: ${theme.spacing.subtitleToDepartments};
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 0 16px;
  }
`;

const WorkerSelector = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  margin: 0 15px 48px 15px;
  position: relative;

  @media (max-width: 768px) {
    display: none;
  }
`;

const WorkerCard = styled(motion.div)`
  position: relative;
  width: 400px;
  height: 160px;
  border-radius: 24px;
  overflow: hidden;
  cursor: pointer;
  background: ${props => props.isActive 
    ? theme.workerCard.background.active 
    : theme.workerCard.background.inactive};
  backdrop-filter: blur(${theme.workerCard.glass.blur});
  -webkit-backdrop-filter: blur(${theme.workerCard.glass.blur});
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: ${props => props.$isActive 
    ? theme.workerCard.text.active 
    : theme.workerCard.text.inactive};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    width: 100%;
    height: 120px;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(45deg, 
      ${props => props.isActive 
        ? theme.workerCard.gradient.active 
        : theme.workerCard.gradient.inactive}, 
      transparent);
    opacity: ${props => props.isActive 
      ? theme.workerCard.gradient.opacity.active 
      : theme.workerCard.gradient.opacity.inactive};
    mix-blend-mode: overlay;
    transition: opacity 0.3s ease;
    z-index: 1;
  }

  &:hover {
    transform: ${theme.workerCard.hover.transform};
    box-shadow: ${theme.workerCard.hover.shadow};
    background: ${props => props.$isActive 
      ? theme.workerCard.background.activeHover 
      : theme.workerCard.background.inactiveHover};
  }
`;

const getWorkerImagePosition = (worker) => {
  const positions = {
    'aria': '20%',
    'dan': '30%',
    'solvej': '25%',
    'micah': '30%',  // Higher up position for Micah
    'elen': '30%'    // Higher up position for Elen
  };
  return positions[worker] || '20%';
};

const WorkerImage = styled.div`
  position: absolute;
  inset: 0;
  background-image: ${props => `url(${props.$image})`};
  background-size: cover;
  background-position: center ${props => props.$customPosition || '20%'};
  opacity: ${props => props.$isActive 
    ? theme.workerCard.image.opacity.active 
    : theme.workerCard.image.opacity.inactive};
  transition: opacity 0.3s ease;
  z-index: 0;

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    position: relative;
    border-radius: 12px;
    flex-shrink: 0;
  }

  ${WorkerCard}:hover & {
    opacity: ${props => props.$isActive 
      ? theme.workerCard.image.opacity.activeHover 
      : theme.workerCard.image.opacity.inactiveHover};
  }
`;

const WorkerContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(0, 0, 0, 0.4) 100%
  );
`;

const WorkerRole = styled.h3`
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  opacity: ${props => props.$isActive ? 1 : 0.8};
  text-shadow: ${props => props.$isActive 
    ? '0 2px 4px rgba(0,0,0,0.2)' 
    : 'none'};
  color: white;

  @media (max-width: 768px) {
    font-size: 18px;
    font-weight: 700;
  }
`;

const WorkerName = styled.p`
  font-size: 16px;
  margin: 4px 0 0 0;
  opacity: ${props => props.$isActive ? 0.9 : 0.6};
  text-shadow: ${props => props.$isActive 
    ? '0 1px 2px rgba(0,0,0,0.2)' 
    : 'none'};
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const NavigationButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.isActive ? '#000' : '#f5f5f5'};
  border: none;
  color: ${props => props.isActive ? '#fff' : '#000'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    background: ${props => props.isActive ? '#000' : '#e5e5e5'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ContentWrapper = styled(motion.div)`
  display: flex;
  gap: ${theme.sectionGap};
  margin-top: ${theme.contentTopMargin};
  justify-content: center;
  max-width: 1100px;
  margin-left: 15px;
  margin-right: auto;
  margin-bottom: 80px;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 48px;
  }
`;

const MainCardWrapper = styled.div`
  flex: 0 0 45%;
  
  @media (max-width: 1024px) {
    flex: none;
  }
`;

const FeaturesWrapper = styled.div`
  flex: 0 0 55%;
  margin-top: ${theme.rightSectionTopMargin};
  padding: 0 5px 0 0px;
  
  @media (max-width: 1024px) {
    flex: none;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: ${theme.featureBoxHorizontalGap};
  row-gap: ${theme.featureBoxVerticalGap};
  margin: 12px -8px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const Footer = styled.footer`
  text-align: center;
  padding-top: 64px;
  margin-top: 64px;
  border-top: 1px solid #eee;
  color: #666;
  font-size: 14px;
`;

const DepartmentSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: ${theme.spacing.departmentToWorkers};
  margin-bottom: ${theme.spacing.departmentToWorkers};
  position: relative;
  padding: 4px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.03);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    display: none;
  }
`;

const DepartmentButton = styled.button`
  padding: 12px 24px;
  border-radius: 16px;
  background: ${props => props.$isActive 
    ? 'rgba(0, 0, 0, 0.95)' 
    : 'transparent'};
  color: ${props => props.$isActive ? '#ffffff' : 'rgba(0, 0, 0, 0.7)'};
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 16px;
  font-weight: 500;
  
  &:hover {
    background: ${props => props.$isActive 
      ? 'rgba(0, 0, 0, 0.95)' 
      : 'rgba(0, 0, 0, 0.05)'};
    color: ${props => props.$isActive ? '#ffffff' : 'rgba(0, 0, 0, 0.9)'};
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 10px 16px;
    font-size: 14px;
  }
`;

const MobileWorkerSelector = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 16px;
  }
`;

const MobileWorkerCard = styled(WorkerCard)`
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    padding: 16px;
    height: 80px;
  }
`;

const MobileWorkerInfo = styled.div`
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    margin-left: 16px;
  }
`;

// Preload utility
const preloadMedia = async (workers, t) => {
  const preloadPromises = workers.map(async (worker) => {
    const imageUrl = t(`digitalWorkers.workers.${worker}.profile.media.image`);
    const videoUrl = t(`digitalWorkers.workers.${worker}.profile.media.video`);
    
    const imagePromise = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = reject;
      img.src = imageUrl;
    });

    const videoPromise = videoUrl ? new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.onloadeddata = resolve;
      video.onerror = reject;
      video.src = videoUrl;
    }) : Promise.resolve();

    return Promise.all([imagePromise, videoPromise]);
  });

  try {
    await Promise.all(preloadPromises);
  } catch (error) {
    /* log removed */
  }
};

const DesktopLayout = styled.div`
  display: ${props => props.viewpoint === 'narrow' || props.viewpoint === 'mobile' ? 'none' : 'block'};
`;

const MobileLayout = styled.div`
  display: ${props => props.viewpoint === 'narrow' || props.viewpoint === 'mobile' ? 'block' : 'none'};
`;

const DigitalWorkers = ({ isLoaded, initialWorker }) => {
  const { theme } = useTheme();
  const { t, tSafe, isContentReady } = useLanguage();
  const { currentViewpoint } = useViewpoint();
  const [activeDepartment, setActiveDepartment] = useState('marketing');
  const [activeWorker, setActiveWorker] = useState('aria');
  const [isHovered, setIsHovered] = useState(false);
  const hasInitialized = useRef(false);

  // Enhanced debugging for departments - same as V2
  const departments = useMemo(() => {
    const deptData = tSafe('digitalWorkers.departments', 'object');
    
    if (!deptData || typeof deptData !== 'object') {
      /* log removed */
      return {}; // Safe fallback
    }
    
    const keys = Object.keys(deptData);
    /* log removed */
    return deptData;
  }, [tSafe]);

  // Enhanced debugging for departmentWorkers - same as V2
  const departmentWorkers = useMemo(() => {
    const workers = tSafe(`digitalWorkers.departments.${activeDepartment}.workers`, 'array');
    
    if (!Array.isArray(workers)) {
      /* log removed */
      return []; // Safe fallback
    }
    return workers;
  }, [activeDepartment, tSafe]);

  // Removed unused function scrollToWorkerSection

  const scrollToWorkerCard = (workerId) => {
    setTimeout(() => {
      const workerElement = document.querySelector(`[data-employee-id="${workerId}"]`);
      if (workerElement) {
        workerElement.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 200);
  };

  const handleWorkerClick = (worker) => {
    setActiveWorker(worker);
    // Update URL to include worker name without triggering scroll
    const newPath = `/digital-workers/${worker}`;
    window.history.pushState(null, '', newPath);
  };

  // Initialize with the correct department based on initial worker - only when content is ready
  useEffect(() => {
    if (!isContentReady || hasInitialized.current) return;

    /* log removed */
    
    // Check URL first
    const path = window.location.pathname;
    const match = path.match(/^\/digital-workers(?:\/([^/]+))?$/);
    
    if (match && match[1]) {
      // Find the department containing this worker
      for (const [dept, data] of Object.entries(departments)) {
        /* log removed */
        if (data.workers && data.workers.includes(match[1])) {
          setActiveDepartment(dept);
          setActiveWorker(match[1]);
          hasInitialized.current = true;
          scrollToWorkerCard(match[1]);
          return;
        }
      }
    } else if (initialWorker) {
      // Fall back to initialWorker prop if URL doesn't have a worker
      for (const [dept, data] of Object.entries(departments)) {
        if (data.workers && data.workers.includes(initialWorker)) {
          setActiveDepartment(dept);
          setActiveWorker(initialWorker);
          hasInitialized.current = true;
          return;
        }
      }
    }
    
    // Default initialization: default to marketing department
    const departmentEntries = Object.entries(departments);
    const marketingDept = departments.marketing;
    
    if (marketingDept && marketingDept.workers && marketingDept.workers.length > 0) {
      setActiveDepartment('marketing');
      setActiveWorker(marketingDept.workers[0]); // Set first worker in marketing
      hasInitialized.current = true;
      /* log removed */
      return;
    }
    
    // Fallback: pick the first department that has workers if marketing doesn't exist
    for (const [dept, data] of departmentEntries) {
      if (data.workers && data.workers.length > 0) {
        setActiveDepartment(dept);
        setActiveWorker(data.workers[0]);
        hasInitialized.current = true;
        /* log removed */
        return;
      }
    }
  }, [initialWorker, tSafe, isContentReady, departments]);

  // Reset hover state when worker changes
  useEffect(() => {
    setIsHovered(false);
  }, [activeWorker]);

  // Handle URL changes
  useEffect(() => {
    const handleUrlChange = () => {
      if (!isLoaded.current || !isContentReady) return; // Skip if content isn't loaded yet or not ready

      const path = window.location.pathname;
      const match = path.match(/^\/digital-workers(?:\/([^/]+))?$/);
      
      if (match && match[1]) {
        // Find the department containing this worker
        for (const [dept, data] of Object.entries(departments)) {
          if (data.workers && data.workers.includes(match[1])) {
            setActiveDepartment(dept);
            setActiveWorker(match[1]);
            scrollToWorkerCard(match[1]);
            break;
          }
        }
      }
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, [tSafe, isLoaded, isContentReady, departments]);

  // Preload media assets when component mounts
  useEffect(() => {
    if (!isContentReady) return; // Wait for content to be ready
    
    try {
      const allWorkers = Object.values(departments)
        .filter(dept => {
          const isValid = dept && dept.workers && Array.isArray(dept.workers);
          return isValid;
        })
        .flatMap(dept => dept.workers);
      
      /* log removed */
      preloadMedia(allWorkers, t);
    } catch (error) {
      /* log removed */
    }
  }, [t, tSafe, isContentReady, departments]);

  const handleDepartmentClick = (department) => {
    setActiveDepartment(department);
    const workers = tSafe(`digitalWorkers.departments.${department}.workers`, 'array');
    /* log removed */
    
    if (Array.isArray(workers) && workers.length > 0) {
      const worker = workers[0];
      setActiveWorker(worker);
      // Update URL when changing department (select first worker)
      const newPath = `/digital-workers/${worker}`;
      window.history.pushState(null, '', newPath);
    }
  };

  const renderWorkerSection = (workerKey) => {
    try {
      
      return (
        <ContentWrapper
          theme={theme}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
          data-employee-id={workerKey}
        >
          <MainCardWrapper>
            <WorkerProfile 
              workerKey={workerKey} 
              shouldPlayVideo={isHovered}
            />
          </MainCardWrapper>
          
          <FeaturesWrapper theme={theme}>
            <FeaturesGrid theme={theme}>
              {[1, 2, 3, 4].map((num) => {
                try {
                  const title = t(`digitalWorkers.workers.${workerKey}.features.feature${num}.title`);
                  const description = t(`digitalWorkers.workers.${workerKey}.features.feature${num}.description`);
                  const image = t(`digitalWorkers.workers.${workerKey}.features.feature${num}.image`);
                  
                  /* log removed */
                  
                  return (
                    <FeatureBox
                      key={num}
                      title={title}
                      description={description}
                      image={image}
                    />
                  );
                } catch (error) {
                  /* log removed */
                  return (
                    <div key={num} style={{ padding: '20px', background: '#ffe6e6', borderRadius: '8px' }}>
                      <strong>Error loading feature {num}</strong>
                      <pre>{error.message}</pre>
                    </div>
                  );
                }
              })}
            </FeaturesGrid>
          </FeaturesWrapper>
        </ContentWrapper>
      );
    } catch (error) {
      /* log removed */
      return (
        <div style={{ padding: '40px', background: '#ffe6e6', borderRadius: '8px', margin: '20px' }}>
          <h3>Error rendering worker section</h3>
          <p><strong>Worker:</strong> {workerKey}</p>
          <p><strong>Error:</strong> {error.message}</p>
          <pre>{error.stack}</pre>
        </div>
      );
    }
  };

  return (
    <Section id="digitalWorkers" theme={theme}>
      <Container>
        <Title theme={theme}>{t('digitalWorkers.title')}</Title>
        <Subtitle theme={theme}>{t('digitalWorkers.subtitle')}</Subtitle>
        
        <DesktopLayout viewpoint={currentViewpoint}>
          <DepartmentSelector theme={theme}>
            {Object.keys(departments).map((department) => {
              try {
                return (
                  <DepartmentButton
                    key={department}
                    $isActive={activeDepartment === department}
                    onClick={() => handleDepartmentClick(department)}
                  >
                    {t(`digitalWorkers.departments.${department}.name`)}
                  </DepartmentButton>
                );
              } catch (error) {
                /* log removed */
                return (
                  <div key={department} style={{ padding: '10px', background: '#ffe6e6' }}>
                    Error: {department}
                  </div>
                );
              }
            })}
          </DepartmentSelector>
          
          <WorkerSelector>
            {departmentWorkers.map((worker) => {
              try {
                return (
                  <WorkerCard
                    key={worker}
                    className="worker-card-hover"
                    $isActive={activeWorker === worker}
                    onClick={() => handleWorkerClick(worker)}
                    onMouseEnter={() => worker === activeWorker && setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    theme={theme}
                  >
                    <WorkerImage
                      $image={(function(){
                        const url = t(`digitalWorkers.workers.${worker}.profile.media.image`);
                        return typeof url === 'string' && /^https?:\/\//.test(url) ? url : '';
                      })()}
                      $isActive={activeWorker === worker}
                      theme={theme}
                      $customPosition={getWorkerImagePosition(worker)}
                    />
                    <WorkerContent>
                      <WorkerRole $isActive={activeWorker === worker}>
                        {t(`digitalWorkers.workers.${worker}.profile.role`)}
                      </WorkerRole>
                    </WorkerContent>
                  </WorkerCard>
                );
              } catch (error) {
                /* log removed */
                return (
                  <div key={worker} style={{ padding: '20px', background: '#ffe6e6' }}>
                    Error: {worker} - {error.message}
                  </div>
                );
              }
            })}
          </WorkerSelector>

          <AnimatePresence mode="wait">
            {renderWorkerSection(activeWorker)}
          </AnimatePresence>
        </DesktopLayout>

        <MobileLayout viewpoint={currentViewpoint}>
          <ImmersiveWorkerView />
        </MobileLayout>
      </Container>
    </Section>
  );
};

export default DigitalWorkers;